import CheckIcon from "@mui/icons-material/CheckRounded";
import { Tooltip } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride, GridKeyValue } from "@mui/x-data-grid-premium";
import type { Maybe } from "@entrilia/shared/types";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type {
  DocumentInfo,
  InvestorDeliveryInfo,
  MessagePublishStatus,
} from "../../../../../api/types/documentCollectionTypes";
import CategoryTag from "../../../../common/CategoryTag";
import DocumentPortalStatusTag from "../../DocumentPortalStatusTag";
import DocumentActionsCell from "./DocumentActionsCell";
import DocumentStatus from "./DocumentStatus";
import FundLevelDataName from "./FundLevelDataName";
import FundLevelGroupName from "./FundLevelGroupName";
import { formatDocumentAccess, getDocumentAccessOption, type DocumentAccessOption } from "./documentAccessHelper";

export interface InvestorLevelRowModel {
  id: string;
  postMessageRequestId: string;
  publishingStatus: MessagePublishStatus;
  publishToInvestorPortal: boolean;
  category: string;
  categoryId: string;
  investorTitle: string;
  fundName: string;
  name: string;
  isViewed: boolean;
  viewedAt?: string;
  documentAccess: DocumentAccessOption;
}

export interface FundLevelRowModel {
  id: string;
  group: string;
  postMessageRequestId: string;
  publishingStatus: MessagePublishStatus;
  publishToInvestorPortal: boolean;
  name: string;
  fundName: string;
  category: string;
  isViewed: boolean;
  viewedAt?: string;
  investor: string;
  documentAccess: DocumentAccessOption;
}

export const investorLevelColumns: GridColDef<InvestorLevelRowModel>[] = [
  {
    field: nameof<InvestorLevelRowModel>("name"),
    headerName: "Name",
    flex: 3,
  },
  {
    field: nameof<InvestorLevelRowModel>("investorTitle"),
    headerName: "Investor",
    flex: 2,
    minWidth: 120,
  },
  {
    field: nameof<InvestorLevelRowModel>("fundName"),
    headerName: "Fund",
    flex: 2,
    minWidth: 120,
  },
  {
    field: nameof<InvestorLevelRowModel>("publishToInvestorPortal"),
    headerName: "Investor Portal",
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => <DocumentPortalStatusTag isPublished={row.publishToInvestorPortal} />,
  },
  {
    field: nameof<InvestorLevelRowModel>("publishingStatus"),
    headerName: "Status",
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => <DocumentStatus status={row.publishingStatus} />,
  },
  {
    field: nameof<InvestorLevelRowModel>("category"),
    headerName: "Category",
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => <CategoryTag externalCategoryId={row.categoryId} />,
  },
  {
    field: nameof<InvestorLevelRowModel>("documentAccess"),
    headerName: "File Access",
    flex: 1,
    minWidth: 120,
    valueFormatter: formatDocumentAccess,
  },
  {
    field: nameof<InvestorLevelRowModel>("isViewed"),
    headerName: "Viewed",
    type: "boolean",
    flex: 0.5,
    minWidth: 68,
    renderCell: ({ row }) =>
      row.isViewed ? (
        <Tooltip arrow title={row.viewedAt} disableHoverListener={!row.viewedAt}>
          <CheckIcon color="primary" />
        </Tooltip>
      ) : null,
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => (
      <DocumentActionsCell
        id={row.id}
        postMessageRequestId={row.postMessageRequestId}
        name={row.name}
        publishingStatus={row.publishingStatus}
        currentDocumentsAccess={[row.documentAccess]}
      />
    ),
  },
];

export const getInvestorLevelRows = (documents: DocumentInfo[]): InvestorLevelRowModel[] =>
  documents.map((doc) => ({
    id: doc.id,
    postMessageRequestId: doc.postMessageRequestId,
    publishingStatus: doc.publishingStatus,
    publishToInvestorPortal: doc.publishToInvestorPortal !== false, // show Published when no value is provided
    name: doc.name,
    investorTitle: doc.investorTitle || "",
    fundName: doc.fundName || "",
    category: doc.category,
    categoryId: doc.categoryId,
    isViewed: doc.isViewed,
    viewedAt: doc.viewedAt ? convertISODateTime(doc.viewedAt) : undefined,
    documentAccess: getDocumentAccessOption(doc.accessOptions),
  }));

const groupingKeySeparator = "|||";

const createGroupingKey = (document: DocumentInfo) =>
  [
    document.id,
    document.postMessageRequestId,
    document.name,
    document.category,
    document.categoryId,
    document.publishingStatus,
    document.publishToInvestorPortal !== false, // show Published when no value is provided
    getDocumentAccessOption(document.accessOptions),
  ].join(groupingKeySeparator);

const parseGroupingKey = (groupingKey: Maybe<GridKeyValue>) => {
  const [
    id,
    postMessageRequestId,
    name,
    category,
    categoryId,
    publishingStatus,
    publishToInvestorPortal,
    documentAccess,
  ] = (groupingKey?.toString() ?? "").split(groupingKeySeparator);

  return {
    id,
    postMessageRequestId,
    name,
    category,
    categoryId,
    publishingStatus,
    publishToInvestorPortal,
    documentAccess,
  };
};

const createRowId = (document: DocumentInfo, delivery: InvestorDeliveryInfo) =>
  `${document.id}${groupingKeySeparator}${delivery.investorId}`;

export const parseRowId = (rowId: string) => {
  const [documentId, investorId] = rowId.split(groupingKeySeparator);
  return { documentId, investorId };
};

export const fundLevelGroupingColumn: GridGroupingColDefOverride<FundLevelRowModel> = {
  headerName: "Name",
  flex: 2,
  hideDescendantCount: true,
  renderCell: ({ id, rowNode, row }) =>
    rowNode.type === "group" ? (
      <FundLevelGroupName id={id} rowNode={rowNode} text={parseGroupingKey(rowNode.groupingKey).name || ""} />
    ) : (
      <FundLevelDataName text={row.investor} />
    ),
};

export const fundLevelColumns: GridColDef<FundLevelRowModel>[] = [
  {
    field: nameof<FundLevelRowModel>("fundName"),
    headerName: "Fund",
    flex: 2,
    minWidth: 120,
  },
  {
    field: nameof<FundLevelRowModel>("publishToInvestorPortal"),
    headerName: "Investor Portal",
    flex: 1,
    minWidth: 120,
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        const isPublished = parseGroupingKey(rowNode.groupingKey).publishToInvestorPortal === "true";

        return <DocumentPortalStatusTag isPublished={isPublished} />;
      }

      return null;
    },
  },
  {
    field: nameof<FundLevelRowModel>("publishingStatus"),
    headerName: "Status",
    flex: 1,
    minWidth: 120,
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        const status = parseGroupingKey(rowNode.groupingKey).publishingStatus as MessagePublishStatus | undefined;
        if (status) {
          return <DocumentStatus status={status} />;
        }
      }

      return null;
    },
  },
  {
    field: nameof<FundLevelRowModel>("category"),
    headerName: "Category",
    flex: 1,
    minWidth: 120,
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        const { categoryId } = parseGroupingKey(rowNode.groupingKey);
        if (categoryId) {
          return <CategoryTag externalCategoryId={categoryId} />;
        }
      }

      return null;
    },
  },
  {
    field: nameof<FundLevelRowModel>("documentAccess"),
    headerName: "File Access",
    flex: 1,
    minWidth: 120,
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        const { documentAccess } = parseGroupingKey(rowNode.groupingKey);
        return documentAccess ? formatDocumentAccess(documentAccess as DocumentAccessOption) : null;
      }

      return null;
    },
  },
  {
    field: nameof<FundLevelRowModel>("isViewed"),
    headerName: "Viewed",
    type: "boolean",
    flex: 0.5,
    minWidth: 68,
    renderCell: ({ row }) =>
      row.isViewed ? (
        <Tooltip arrow title={row.viewedAt} disableHoverListener={!row.viewedAt}>
          <CheckIcon color="primary" />
        </Tooltip>
      ) : null,
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ rowNode }) => {
      if (rowNode.type === "group") {
        const { id, postMessageRequestId, name, publishingStatus, documentAccess } = parseGroupingKey(
          rowNode.groupingKey
        );

        if (!id || !postMessageRequestId || !name || !publishingStatus) {
          return null;
        }

        return (
          <DocumentActionsCell
            id={id}
            postMessageRequestId={postMessageRequestId}
            name={name}
            publishingStatus={publishingStatus as MessagePublishStatus}
            currentDocumentsAccess={[documentAccess as DocumentAccessOption]}
          />
        );
      }
      return null;
    },
  },
];

const unwrapDocument = (document: DocumentInfo): FundLevelRowModel[] => {
  const result: FundLevelRowModel[] = [];

  const allInvestors = new Map<string, number>();
  document.investorDeliveries.forEach((delivery) => {
    // To ensure unique path in case there are multiple investors with the same title, preserving visible representation of title
    const investorTitleCounter = allInvestors.get(delivery.investorTitle) ?? 0;
    allInvestors.set(delivery.investorTitle, investorTitleCounter + 1);
    const investorTitle = delivery.investorTitle.padEnd(delivery.investorTitle.length + investorTitleCounter, " ");

    result.push({
      id: createRowId(document, delivery),
      group: createGroupingKey(document),
      postMessageRequestId: document.postMessageRequestId,
      publishingStatus: document.publishingStatus,
      publishToInvestorPortal: document.publishToInvestorPortal !== false, // show Published when no value is provided
      name: document.name,
      investor: investorTitle,
      fundName: delivery.fundName,
      category: document.category,
      isViewed: delivery.isViewed,
      viewedAt: delivery.viewedAt ? convertISODateTime(delivery.viewedAt) : undefined,
      documentAccess: getDocumentAccessOption(document.accessOptions),
    });
  });

  return result;
};

export const getFundLevelRows = (documents: DocumentInfo[]): FundLevelRowModel[] => documents.flatMap(unwrapDocument);
